<template>
    <div class="min-h-screen bg-surface-muted">
        <div class="flex min-h-screen">
            <section class="relative flex min-h-screen basis-full items-center justify-center bg-white px-8 py-12 lg:basis-1/2">
                <div class="w-full max-w-[36rem]">
                    <div>
                        <h1 class="text-4xl font-semibold text-gray-900">
                            {{ loading ? $t('Securely shared note') : displayTitle }}
                        </h1>
                        <p class="mt-4 text-[0.95rem] leading-[1.6] text-ink">
                            {{ displayDescription }}
                        </p>
                    </div>

                    <div class="mt-8">
                        <div v-if="loading" class="rounded border border-bordergray-100 bg-gray-50 px-5 py-4 text-sm text-gray-500">
                            {{ $t('Loading secure note') }}...
                        </div>

                        <div v-else-if="errorMessage" class="rounded border border-bordergray-100 bg-gray-50 px-5 py-4 text-sm text-gray-500">
                            {{ translatedErrorMessage }}
                        </div>

                        <div v-else class="rounded border border-bordergray-100 bg-white p-6">
                            <p v-if="note.description" class="mb-4 text-sm leading-[1.6] text-ink whitespace-pre-wrap">
                                {{ note.description }}
                            </p>
                            <pre class="m-0 whitespace-pre-wrap break-words rounded border border-bordergray-100 bg-gray-50 p-4 text-sm text-gray-900">{{ note.content }}</pre>
                        </div>
                    </div>

                </div>
            </section>

            <aside class="relative hidden min-h-screen overflow-hidden bg-[#202830] bg-cover bg-center bg-no-repeat lg:block lg:basis-1/2" :style="visualPanelStyle">
                <div class="auth-shell__visual-overlay absolute inset-0"></div>
                <div class="relative z-[1] flex h-full flex-col p-14 text-white">
                    <img :src="visualLogo" :alt="$store.state.settings.name" class="block h-7 w-auto self-start object-contain">
                    <div class="mt-auto max-w-[30rem]">
                        <img :src="visualMark" :alt="$store.state.settings.name" class="w-16 h-16 -ml-3 object-contain">
                        <p class="mt-4 text-base leading-relaxed text-white/80" v-html="visualText"></p>
                    </div>
                </div>
            </aside>
        </div>
    </div>
</template>

<script>
export default {
    name: "vault-share-open",
    metaInfo() {
        return {
            title: this.$t('Securely shared note').toString()
        };
    },
    data() {
        return {
            loading: true,
            errorMessage: '',
            consumeInFlight: false,
            note: {
                title: '',
                description: '',
                content: '',
            },
        };
    },
    computed: {
        displayTitle() {
            if (this.errorMessage) {
                return this.$t('Securely shared note').toString();
            }

            return this.note.title || this.$t('Securely shared note').toString();
        },
        displayDescription() {
            return this.$t('This note can only be opened once and expires afterwards').toString();
        },
        translatedErrorMessage() {
            return this.errorMessage ? this.$t(this.errorMessage).toString() : '';
        },
        visualPanelStyle() {
            const backgroundImage = this.$store.state.settings.background;
            const style = {};

            if (backgroundImage) {
                style.backgroundImage = 'url(' + backgroundImage + ')';
            }

            return style;
        },
        visualLogo() {
            return this.$store?.state?.settings?.logo_invert
                || this.$store?.state?.settings?.logo
                || '/images/default/logo_invert.svg';
        },
        visualMark() {
            return this.$store?.state?.settings?.logo_toggle_invert
                || this.$store?.state?.settings?.logo_toggle
                || '/images/default/icon.png';
        },
        visualText() {
            return 'CORPRED<sup class="auth-registered-mark">&reg;</sup> entwickelt digitale Applikationen für effiziente Prozesse, klare Kommunikation und zukunftsweisende Arbeitsabläufe.';
        }
    },
    mounted() {
        this.consumeShareLink();
    },
    beforeRouteLeave(to, from, next) {
        const selector = String(this.$route.params.selector || '').trim();
        this.clearConsumedNote(selector);
        next();
    },
    methods: {
        shareNoteStorageKey(selector) {
            return 'vault-share-note:' + selector;
        },
        readHashKey() {
            const rawHash = String(window.location.hash || '').replace(/^#/, '').trim();
            if (rawHash === '') {
                return '';
            }

            if (rawHash.startsWith('key=')) {
                return decodeURIComponent(rawHash.slice(4)).trim();
            }

            return decodeURIComponent(rawHash).trim();
        },
        persistConsumedNote(selector, note) {
            if (!selector) {
                return;
            }

            // Keep the already opened note only inside this browser tab.
            // This is solely a fallback for forced reloads (for example when the
            // service worker swaps control right after the one-time link was consumed).
            try {
                window.sessionStorage.setItem(this.shareNoteStorageKey(selector), JSON.stringify(note));
            } catch (e) {
                // Ignore storage errors and keep the already loaded note visible.
            }
        },
        restoreConsumedNote(selector) {
            if (!selector) {
                return false;
            }

            try {
                const rawPayload = window.sessionStorage.getItem(this.shareNoteStorageKey(selector));
                if (!rawPayload) {
                    return false;
                }

                const parsedPayload = JSON.parse(rawPayload);
                this.note = {
                    title: String(parsedPayload && parsedPayload.title ? parsedPayload.title : ''),
                    description: String(parsedPayload && parsedPayload.description ? parsedPayload.description : ''),
                    content: String(parsedPayload && parsedPayload.content ? parsedPayload.content : ''),
                };

                return true;
            } catch (e) {
                return false;
            }
        },
        clearConsumedNote(selector) {
            if (!selector) {
                return;
            }

            try {
                window.sessionStorage.removeItem(this.shareNoteStorageKey(selector));
            } catch (e) {
                // Ignore cleanup errors.
            }
        },
        cleanupVisibleUrl(selector) {
            if (!selector) {
                return;
            }

            const cleanPath = '/share/vault/' + selector;
            window.history.replaceState(window.history.state || {}, '', cleanPath);
        },
        consumeShareLink() {
            if (this.consumeInFlight) {
                return;
            }

            const selector = String(this.$route.params.selector || '').trim();
            const queryKey = String(this.$route.query.key || '').trim();
            const hashKey = this.readHashKey();
            const key = queryKey || hashKey;

            if (!selector) {
                this.loading = false;
                this.errorMessage = 'The link is incomplete or invalid';
                return;
            }

            // If a forced reload happens in the same tab immediately after a successful consume,
            // restore the already opened note from sessionStorage instead of re-consuming the link.
            if (!key && this.restoreConsumedNote(selector)) {
                this.loading = false;
                this.errorMessage = '';
                return;
            }

            if (!key) {
                this.loading = false;
                this.errorMessage = 'The link is incomplete or invalid';
                return;
            }

            this.consumeInFlight = true;

            fetch('/api/vault/share/consume', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                body: JSON.stringify({
                    selector: selector,
                    key: key,
                }),
            }).then(async (response) => {
                const payload = await response.json().catch(() => ({}));
                if (!response.ok) {
                    if (this.restoreConsumedNote(selector)) {
                        this.errorMessage = '';
                        return;
                    }

                    this.errorMessage = payload && payload.message
                        ? String(payload.message)
                        : 'The link is invalid or already used';
                    return;
                }

                const consumedNote = {
                    title: String(payload.title || ''),
                    description: String(payload.description || ''),
                    content: String(payload.content || ''),
                };

                this.note = consumedNote;
                this.persistConsumedNote(selector, consumedNote);
                this.cleanupVisibleUrl(selector);
            }).catch(() => {
                this.errorMessage = 'The note could not be loaded';
            }).finally(() => {
                this.loading = false;
                this.consumeInFlight = false;
            });
        },
    },
}
</script>

<style lang="scss">
.auth-registered-mark {
    font-size: 0.52em;
    line-height: 0;
    vertical-align: super;
}

.auth-shell__visual-overlay {
    background:
        linear-gradient(145deg, rgba(32, 40, 48, 0.8), rgba(20, 25, 31, 0.9)),
        linear-gradient(180deg, rgba(32, 40, 48, 0.15), rgba(20, 25, 31, 0.62));
}
</style>
